import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, Menu, X, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const { getCartCount } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mobileOpenCat, setMobileOpenCat] = useState(null);
  const cartCount = getCartCount();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const all = response.data;
      const parents = all.filter((c) => !c.parent_id);
      setCategories(
        parents.map((p) => ({
          ...p,
          subcategories: all.filter((c) => c.parent_id === p.id),
        }))
      );
    } catch (e) {
      console.error('Failed to load categories:', e);
      setCategories([]);
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center h-14 gap-1">
          {/* Desktop horizontal nav */}
          <div className="hidden lg:flex flex-1 items-center gap-1 h-full">
            <Link
              to="/"
              className="px-4 h-full flex items-center text-sm font-bold text-gray-800 hover:text-cyan-600 hover:bg-gray-50 transition"
              data-testid="nav-home"
            >
              Accueil
            </Link>

            {categories.map((category) => (
              <div key={category.id} className="relative group h-full">
                <Link
                  to={`/products?category=${category.id}`}
                  className="px-4 h-full flex items-center gap-1 text-sm font-bold text-gray-800 hover:text-cyan-600 hover:bg-gray-50 transition"
                  data-testid={`nav-cat-${category.slug}`}
                >
                  {category.icon && <span className="mr-1">{category.icon}</span>}
                  {category.name}
                  {category.subcategories?.length > 0 && (
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                  )}
                </Link>

                {category.subcategories?.length > 0 && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-white border border-gray-200 rounded-b-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                    <div className="py-2 max-h-96 overflow-y-auto">
                      <Link
                        to={`/products?category=${category.id}`}
                        className="block px-4 py-2 text-sm font-medium text-cyan-600 hover:bg-cyan-50 transition"
                      >
                        → Tout {category.name}
                      </Link>
                      <div className="border-t border-gray-100 my-1"></div>
                      {category.subcategories.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/products?category=${sub.id}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-600 transition"
                          data-testid={`nav-subcat-${sub.slug}`}
                        >
                          {sub.icon && <span className="mr-2">{sub.icon}</span>}
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <Link
              to="/products"
              className="px-4 h-full flex items-center text-sm font-bold text-gray-800 hover:text-cyan-600 hover:bg-gray-50 transition"
            >
              Tous les Produits
            </Link>
            <Link
              to="/contact"
              className="px-4 h-full flex items-center text-sm font-bold text-gray-800 hover:text-cyan-600 hover:bg-gray-50 transition"
            >
              Contact
            </Link>
          </div>

          {/* Mobile toggle (left on mobile) */}
          <button
            className="lg:hidden text-gray-700 p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Brand pill (visible on mobile when nav hidden) */}
          <Link
            to="/"
            className="lg:hidden flex-1 text-center font-extrabold text-gray-900 tracking-wide"
            data-testid="nav-brand-mobile"
          >
            GC <span className="text-cyan-600">STORE</span>
          </Link>

          {/* Cart */}
          <Link to="/cart" className="relative ml-auto p-2" data-testid="cart-icon">
            <ShoppingCart className="w-6 h-6 text-gray-700 hover:text-cyan-600 transition" />
            {cartCount > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center neon-cyan"
                data-testid="cart-count"
              >
                {cartCount}
              </span>
            )}
          </Link>
        </div>

        {/* Mobile horizontal categories strip — always visible */}
        {categories.length > 0 && (
          <div className="lg:hidden -mx-4 px-4 py-2 border-t border-gray-100 overflow-x-auto whitespace-nowrap">
            <div className="flex gap-2 min-w-max">
              <Link
                to="/products"
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-900 text-white text-xs font-bold active:scale-95 transition"
                data-testid="mobile-strip-all"
              >
                Tous
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/products?category=${c.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-gray-300 text-gray-800 text-xs font-bold active:bg-cyan-50 active:border-cyan-400 transition"
                  data-testid={`mobile-strip-cat-${c.slug}`}
                >
                  {c.icon} {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-3 border-t border-gray-200">
            <Link
              to="/"
              className="block py-3 px-2 text-gray-800 hover:text-cyan-600 font-bold"
              onClick={() => setIsMenuOpen(false)}
            >
              Accueil
            </Link>
            {categories.map((category) => (
              <div key={category.id} className="border-t border-gray-100">
                <div className="flex items-stretch">
                  {/* Category name → navigates */}
                  <Link
                    to={`/products?category=${category.id}`}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex-1 py-3 px-2 text-gray-800 font-bold active:bg-gray-100"
                    data-testid={`mobile-nav-cat-${category.slug}`}
                  >
                    {category.icon} {category.name}
                  </Link>
                  {/* Chevron → only expands subcategories */}
                  {category.subcategories?.length > 0 && (
                    <button
                      onClick={() =>
                        setMobileOpenCat(
                          mobileOpenCat === category.id ? null : category.id
                        )
                      }
                      className="px-4 flex items-center text-gray-500 active:bg-gray-100"
                      aria-label={`Voir sous-catégories ${category.name}`}
                    >
                      <ChevronDown
                        className={`w-5 h-5 transition-transform ${
                          mobileOpenCat === category.id ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </div>
                {mobileOpenCat === category.id && category.subcategories?.length > 0 && (
                  <div className="pl-6 pb-2 bg-gray-50">
                    {category.subcategories.map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/products?category=${sub.id}`}
                        className="block py-2 px-2 text-sm text-gray-700 hover:text-cyan-600 active:bg-gray-100"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {sub.icon} {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/products"
              className="block py-3 px-2 text-gray-800 font-bold border-t border-gray-100"
              onClick={() => setIsMenuOpen(false)}
            >
              Tous les Produits
            </Link>
            <Link
              to="/contact"
              className="block py-3 px-2 text-gray-800 font-bold border-t border-gray-100"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
